<template>
  <DefaultLayout>
    <div class="rounded-md h-full px-5 py-3">
      <div v-if="loading_ld || getCourseLoading" key="loading" class="h-full mt-6">
        <div class="flex flex-col justify-center items-center">
          <p class="mb-2.5">Loading RoundTable</p>
          <pl-spinner />
        </div>
      </div>
      <div v-else @keyup.esc="isFullscreen ? isFullscreen = !isFullscreen : isFullscreen = isFullscreen">
        <header class="mb-3 relative">
          <div class="flex flex-col md:flex-row justify-between space-y-2 md:space-y-0 md:items-center">
            <div class="flex space-x-3 items-center">
              <div class="ml-2" v-if="rt_integration">
                <a :href="assignmentUrl" target="blank">
                  <img :src="assignmentIcon" width="22px" height="22px" class="rounded-full" />
                </a>
              </div>
              <DropdownMenu v-if="getCurrentDiscussion.sequence_id !== null">
                <template v-slot:toggle>
                  <button class="bg-indigo-500 rounded-full py-2 px-4 flex items-center justify-between space-x-2">
                    <img src="/static/roundtable_icons/sequence_icon.svg" class="w-4 h-4" />
                    <i class="fa-solid text-xs fa-chevron-down text-indigo-50 hover:text-indigo-100"></i>
                  </button>
                </template>
                <template v-slot:menu-content>
                  <ul class="absolute z-50 bg-white border border-solid border-gray-200 max-w-lg rounded-lg mt-2 shadow-sm">
                    <li 
                      v-for="(roundtable, index) in getCurrentDiscussion.roundtables_sequence" 
                      :key="index" 
                      class="border-b border-gray-200 py-2 px-3 hover:bg-gray-100 cursor-pointer"
                      :class="[{'border-none': index + 1 === getCurrentDiscussion.roundtables_sequence.length }, {'bg-gray-100': roundtable.id === $route.params.liveDiscussionID }]"
                      @click="handleClickSequenceRoundTable(roundtable.id, roundtable.type)"
                    >
                      <div class="flex justify-start items-center space-x-2">
                        <div class="w-7 h-7">
                          <div 
                            class="flex items-center justify-center rounded-full h-7 w-7" 
                            :class="[{'bg-blue': roundtable.type === 'written' || roundtable.type === 'roundtable', 'bg-green-light': roundtable.type === 'verbal' || roundtable.type === 'live_discussion'}]"
                          >
                            <img 
                              :class="[{ 'w-3 h-3': roundtable.type === 'written' }, { 'w-3 pt-0.5 h-3': roundtable.type === 'verbal' }]"
                              :src="roundtable.type === 'written' || roundtable.type === 'roundtable' ? '/static/roundtable_icons/written_icon.svg' : '/static/roundtable_icons/verbal_icon.svg'"
                              class="w-3 h-3"
                            />
                          </div>
                        </div>
                        <h6 class="truncate">{{ roundtable.title }}</h6>
                      </div>
                    </li>
                  </ul>
                </template>
              </DropdownMenu>
              <div :title="getCurrentDiscussion.title" class="text-2xl font-extrabold font-manrope cursor-default text-gray-800 antialiased font-bold">
                {{ getCurrentDiscussion.title }}
              </div>
              <div v-if="getCurrentDiscussion.sequence_id" class="bg-indigo-100 text-xs border border-solid border-indigo-500 text-indigo-500 rounded-full px-2 py-0.5">
                <div class="flex justify-between items-center">
                  <span>{{ current_sequence_number + 1 }}</span>
                  <span class="ml-0.5 mr-1">of</span>
                  <span>{{ getCurrentDiscussion.roundtables_sequence.length }}</span>
                </div>
              </div>
            </div>
            <div class="flex items-center space-x-3">
              <div v-if="showQuestionIcon" @click="showRTWelcomeModal" class="cursor-pointer text-base text-gray-600">
                <i class="fa-solid fa-circle-info text-base"></i>
              </div>
              <button
                v-if="hasTeacherPermissionsInCourse"
                class="px-5 font-manrope py-2 bg-pinky hover:bg-pinky-dark rounded-full text-white font-semibold text-sm disabled:opacity-50"
                @click="isInviteStudentModalOpen = true"
              >
                <span class="truncate">Invite Students</span>
              </button>
              <div v-if="hasTeacherPermissionsInCourse">
                <LiveDiscussionMenu
                  @showDuplicateModal="show_duplicate_modal = true"
                  @showEditLrtModal="show_edit_lrt_modal = true"
                  @showFollowUp="(type) => handleOpenStartFollowUP(type)"
                  @rtIntegrationAssignment="(value) => (show_integrations_modal = value)"
                  @handleDeleteRoundTable="show_delete_live_rt_modal = true"
                  @handleOpenExportData="show_export_data_modal = true"
                  @handleRemoveFromSequence="is_to_remove_roundtable_from_sequence = true"
                />  
              </div>
            </div>
          </div>
        </header>
        <div class="sm:flex sm:flex-row items-start space-x-1.5" :class="{'absolute h-screen bg-gray-50 top-0 left-0 right-0 bottom-0 z-50 overflow-y-auto': isFullscreen}">
          <div id="discussion-main" class="flex flex-col tab-body rounded-lg w-full">
            <header id="tabs" :class="{'bg-white border border-solid border-gray-200 rounded-t-lg': !isFullscreen}" class="flex justify-between items-center w-full pt-2.5 pr-5">
              <div class="flex items-center rounded-t-lg overflow-x-auto font-manrope">
                <div @click="goToTab('prompt')" :class="{'border-primary text-primary border-b': tab === 'prompt'}" class="h-7 px-5 select-none cursor-pointer">
                  Prompt
                </div>
                <div v-if="hasTeacherPermissionsInCourse" @click="goToTab('settings')" :class="{'border-primary text-primary border-b': tab === 'settings'}" class="h-7 select-none px-5 cursor-pointer">
                  Settings
                </div>
                <div @click="goToTab('discussion')" :class="{'border-primary text-primary border-b': tab === 'discussion'}" class="h-7 select-none px-5 cursor-pointer">
                  Discussion
                </div>
                <div @click="goToTab('transcriptions')" :class="{'border-primary text-primary border-b': tab === 'transcriptions'}" class="h-7 select-none px-5 cursor-pointer">
                  Transcription
                </div>
                <div @click="goToTab('summary')" :class="{'border-primary text-primary border-b': tab === 'summary'}" class="h-7 px-5 select-none cursor-pointer">
                  Summary
                </div>
                <div v-if="hasTeacherPermissionsInCourse" @click="goToTab('individualSummary')" :class="{'border-primary text-primary border-b': tab === 'individualSummary'}" class="h-7 select-none px-5 cursor-pointer">
                  Table
                </div>
              </div>
              <div v-if="discussion_is_live && tab === 'discussion'" class=" h-7 rounded-md font-manrope -mt-2.5 flex items-center text-xs space-x-2 text-gray-700 px-2.5 py-1.5" style="margin-right: -15px;">
                <i class="fa-regular fa-clock"></i>
                <span>{{ discussion_time }}</span>
              </div>
              <div v-if="tab === 'prompt' && hasTeacherPermissionsInCourse" @click="show_edit_lrt_modal = true" class="px-1 hover:underline text-center cursor-pointer py-0.5 -mt-2 rounded">
                <span>Edit Prompt</span>
              </div>
              <div v-else-if="tab === 'settings' && is_button_save_setting_enable === false" class="-mt-2 flex space-x-1 items-center">
                <button @click="is_confirm_settings_modal_open = true" class="sample flex items-center space-x-2 font-poppins text-blueGray-800 px-1 disabled:opacity-50">
                  <i class="fa-regular fa-floppy-disk text-base"></i> <span class="hover:underline">Save Changes</span>
                </button>
                <button v-if="!is_button_save_setting_enable" @click="forceRerender" class="sample hover:underline text-gray-500 px-1 disabled:opacity-50">
                  Cancel
                </button>
              </div>
            </header>

            <div class="flex flex-col flex-1 min-h-0">
              <div class="flex flex-col flex-1 min-h-0 relative rounded-b-lg bg-white border-r border-l border-b border-solid border-gray-200">
                <Component
                  v-if="getCurrentDiscussion.id && component_to_render !== 'LDDiscussionTab'"
                  :is="component_to_render"
                  :key="componentKey"
                  :isFullscreen="isFullscreen"
                  ref="childComponent"
                  @editAssessmentPoint="editAssessmentPoint"
                  @deleteAssessmentPoint="deleteAssessmentPoint"
                  @cancelSettingsChanges="forceRerender"
                  @openSendNudge="openSendNudgeModal"
                  @toggleFullscreen="toggleFullscreen"
                  @handleSettingsChange="(value) => (is_button_save_setting_enable = value)"
                  class="flex flex-col flex-1 min-h-0 px-3 py-3"
                  :class="{'justify-between': isFullscreen && tab === 'discussion'}"
                />

                <div v-if="!isOpenParticipant && tab === 'discussion'" class="absolute z-50 cursor-pointer -right-2.5 top-1/4 bg-white hover:bg-gray-50 border border-solid rounded-full w-7 h-7 border-gray-200 shadow">
                  <div @click="onChangeSidebar(toogle_participations)" class="flex justify-center text-gray-500 items-center h-full" style="font-size: 10px;">
                    <i :class="{'fa-chevron-right': toogle_participations || toogle_notepad, 'fa-chevron-left': !toogle_participations || !toogle_notepad}" class="fa-solid"></i>
                  </div>
                </div>

                <div v-if="tab === 'transcriptions' && getCurrentDiscussion.transcriptions && (isGenieRecapOpen || hasTeacherPermissionsInCourse || (!hasTeacherPermissionsInCourse && getCurrentDiscussion.genie_recap))" class="absolute z-50 cursor-pointer -right-2.5 top-96 bg-white hover:bg-gray-50 border border-solid rounded-full w-7 h-7 border-gray-200 shadow">
                  <div @click="isGenieRecapOpen = !isGenieRecapOpen" class="flex justify-center text-gray-500 items-center h-full" style="font-size: 10px;">
                    <i :class="{'fa-chevron-right': isGenieRecapOpen, 'fa-chevron-left': !isGenieRecapOpen}" class="fa-solid"></i>
                  </div>
                </div>

                <Component
                  v-if="getCurrentDiscussion.id"
                  v-show="component_to_render === 'LDDiscussionTab'"
                  :is="'LDDiscussionTab'"
                  :key="componentKey + 1"
                  @handleSettingsChange="(value) => (is_button_save_setting_enable = value)"
                  :joinedAudioDiscussion="joined_audio_discussion"
                  :rtc="rtc"
                  :screenRTC="screenRTC"
                  :isFullscreen="isFullscreen"
                  ref="childComponent"
                  @editAssessmentPoint="editAssessmentPoint"
                  @deleteAssessmentPoint="deleteAssessmentPoint"
                  @cancelSettingsChanges="forceRerender"
                  @muteAudio="muteAudio"
                  @unmuteAudio="unmuteAudio"
                  @videoOff="videoOff"
                  @videoOn="videoOn"
                  @openSendNudge="openSendNudgeModal"
                  @toggleFullscreen="toggleFullscreen"
                  class="flex flex-col flex-1 min-h-0"
                  :class="{ 'justify-between': isFullscreen && tab === 'discussion' }"
                />
              </div>
            </div>
          </div>

          <div 
            v-if="tab === 'discussion'" 
            class="flex flex-col h-full flex-grow overflow-y-auto relative overflow-x-hidden" 
            :class="{'sm:w-5/12 md:5/12 lg:w-4/12 xl:w-4/12 2xl:w-3/12': toogle_participations === true || isOpenParticipant || toogle_notepad === true, 'max-h-container-participants-fullscreen': isFullscreen, 'max-h-container-participants': !isFullscreen}"
          >
            <div v-if="toogle_participations === true">
              <ListParticipants
                v-if="!isOpenParticipant && hasTeacherPermissionsInCourse"
                :isFullscreen="isFullscreen"
                @openSendNudge="openSendNudgeModal"
                @openParticipantSidebar="openParticipant"
                @setAsModerator="setAsModerator"
              />
            </div>
            <div v-if="toogle_participations === false">
              <ListParticipantsCollapsed 
                v-if="!isOpenParticipant && hasTeacherPermissionsInCourse"
                :isFullscreen="isFullscreen"
                @openSendNudge="openSendNudgeModal"
                @openParticipantSidebar="openParticipant"
                @setAsModerator="setAsModerator"
              />
            </div>
            <div class="relative">
              <Participant
                v-if="isOpenParticipant && hasTeacherPermissionsInCourse"
                :userInformation="studentInformation"
                :isFullscreen="isFullscreen"
                @backToParticipantsList="closeParticipation"
                @openCreateSuggestion="openNewSuggestion"
                @openImport="openImportSuggestions"
                @openDeleteSuggestion="openConfirmDeleteSuggestion"
                @openSendNudge="openSendNudgeModal"
              />
            </div>
            <div v-if="!hasTeacherPermissionsInCourse && toogle_notepad === true">
              <DiscussionSidebarStudentNotes :isFullscreen="isFullscreen" />
            </div>
          </div>
          <div v-if="tab === 'prompt' && !hasTeacherPermissionsInCourse" class="h-full 2xl:w-5/12 xl:w-5/12 lg:w-7/12 md:w-7/12 w-full">
            <PromptSideBarStudentNotepad class="h-full" />
          </div>
          <div v-if="canShowGenieRecap" class="xl:w-4/12 lg:w-5/12 md:w-6/12 w-full sticky top-0 h-full max-h-screen overflow-y-auto">
            <GenieRecapVerbal @startFollowUp="handleStartFollowUp" />
          </div>
          <button @click="show_course_chat = true" v-if="isFullscreen" class="absolute bottom-5 right-5 w-14 h-14 bg-white hover:bg-gray-100 rounded-full shadow-md">
            <div class="text-gray-600 text-xl h-full relative flex justify-center items-center">
              <i class="fa-solid fa-comments"></i>
              <div v-if="message_notifications > 0" class="absolute top-0 right-0 h-3 w-3 rounded-full bg-pinky"></div>
            </div>
          </button>
        </div>
      </div>
    </div>
    <ModalSendNudge
      v-if="isOpenSendNudge === true"
      @closeDialog="closeSendNudgeModal"
      :userInformation="studentInformation"
    />
    <ModalNewSuggestion
      v-if="isOpenNewSuggestion === true"
      @close="isOpenNewSuggestion = false"
      :suggestionToEdit="selectedSuggestion"
    />
    <ImportFeedbackTemplatesModal
      v-if="isOpenImportSuggestions"
      @close="isOpenImportSuggestions = false"
      typeRoundtable="live"
    />
    <ModalDeleteSuggestion
      v-if="isOpenDeleteSuggestion"
      @close="isOpenDeleteSuggestion = false"
      :suggestion="selectedSuggestion"
    />
    <ModalDiscussionIsStarted
      v-if="
        isOpenModalDiscussionStatusChange === true &&
        !hasTeacherPermissionsInCourse
      "
      @close="isOpenModalDiscussionStatusChange = false"
    />
    <roundTable-welcome-modal
      activityType="live"
      @close="closeRTWelcomeModal"
      v-if="show_RT_Welcome_modal"
      :data="welcomeRoundTableData"
    />
    <live-discussion-summary-page
      v-if="show_teacher_summary"
      pageWidth="1200px"
      @close="show_teacher_summary = false"
    />
    <duplicate-roundtable-modal
      @close="show_duplicate_modal = false"
      v-if="show_duplicate_modal"
      :is_live_roundtable="true"
    />

    <integrations-connect-modal
      @close="open_google_classroom_dialog = false"
      v-if="open_google_classroom_dialog"
    />

    <integration-connected-modal
      v-if="is_integration_connected"
      :integration="integration"
      @close="is_integration_connected = false"
    />

    <create-new-point-modal
      :activityType="'verbal'"
      :tag="selected_point_tag"
      :rts="live_discussions_with_point"
      v-if="show_point_creation_modal"
      @deleteAssessmentPoint="deleteAssessmentPoint"
      @close="show_point_creation_modal = false"
      :assessment_scale="selected_point_scale"
    />
    <Draft 
      v-if="isFollowUpOpen === true" 
      :id="selectedRoundTableToFollow.draftId" 
      :type="selectedRoundTableToFollow.type"
      :isFollowUp="true" 
      @close="isFollowUpOpen = false"
    />

    <template v-if="show_alert_assessment_changing">
      <pl-dialog
        size="confirm_size"
        :title="'Heads up!'"
        :disableOutsideClick="true"
        @close="closeAssessmentChangingAlert"
        :showCloseButton="false"
      >
        <div class="px-6 flex flex-col pb-5">
          <div class="flex-grow mb-6">
            <div>
              <p class="mb-4">
                Changing (or deleting) this assessment criteria for this
                RoundTable will change (or delete) it for all of the other
                RoundTables in this course that also use this criteria:
              </p>
            </div>
            <ul>
              <li v-for="live_discussion in live_discussions_with_point" :key="live_discussion.id" class="mb-1">
                <span class="font-semibold font-poppins">- {{ live_discussion.title }}</span>
              </li>
            </ul>
          </div>
          <div class="flex w-full flex-col space-y-4">
            
            <button @click="closeAssessmentChangingAlert" class="bg-primary w-full hover:bg-primary-dark text-white rounded px-3 py-2">
              Okay, Got it
            </button>
            <div class="flex justify-center items-center space-x-2">
              <span class="font-poppins text-gray-800">Do not show this anymore</span>
              <input
                v-model="not_show_heads_up_info"
                type="checkbox"
                class="cursor-pointer"
              />
            </div>
          </div>
        </div>
      </pl-dialog>
    </template>
    <template v-if="show_delete_point_modal">
      <pl-dialog :disableOutsideClick="true" title="Delete Criteria Point" size="confirm_size" @close="closeDeletePointModal">
        <div class="h-full w-full overflow-y-auto">
          <div class="flex flex-col h-full pb-5">
            <div class="overflow-y-auto h-full px-6 mb-4">
              <div class="text-gray-900 font-poppins font-light">
                You are deleting this criteria point from this roundtable. This action cannot be undone.
              </div>
            </div>
            <div class="flex flex-col px-5 flex-grow h-full">
              <div class="flex flex-col w-full space-y-1.5 mb-2">
                <span class="font-poppins text-gray-900">Please type in "confirm" to delete</span>
                <input
                  ref="confirm-input"
                  @keyup.enter="confirmDeleteAssessmentPoint()"
                  placeholder="Type 'confirm' to delete roundTable from the course"
                  type="text"
                  class="py-2 px-3 w-full border border-solid border-gray-200 bg-white rounded-md focus-within:border-red-500"
                />
              </div>
              <div class="flex items-center space-x-2">
                <button
                  @click="show_delete_point_modal = false"
                  class="hover:bg-gray-50 bg-white py-2 flex items-center justify-center space-x-2 text-blueGray-500 font-medium w-full rounded-md border border-solid border-gray-200"
                >
                  <span>Cancel</span>
                </button>
                <button
                  @click="confirmDeleteAssessmentPoint"
                  class="bg-red-500 transition-colors py-2 flex items-center justify-center space-x-2 text-red-50 font-semibold w-full rounded-md border border-solid border-red-500"
                >
                  <span>Confirm</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </pl-dialog>
    </template>
    <connect-integrations-assessment-modal
      v-if="show_integrations_modal"
      @close="show_integrations_modal = false"
      @goToInviteStudents="isInviteStudentModalOpen = true"
    />
    <sync-assessment-data-modal
      v-if="sync_assessment"
      @close="sync_assessment = false"
      :selectedIntegration="selected_integration"
      :activityType="'verbal'"
      :edLink="edLink"
      :logo="logo"
    />
    <ModalSampleRoundTable
      v-if="isModalSampleOpen"
      @close="isModalSampleOpen = false"
      :dataSample="urlVideoSample"
    />
    <EditVerbalDiscussion 
      v-if="show_edit_lrt_modal === true" 
      :id="getCurrentDiscussion.id" 
      @close="show_edit_lrt_modal = false"
    />
    <!-- <edit-live-rt-modal
      v-if="show_edit_lrt_modal"
      :rt_selected="getCurrentDiscussion"
      @close="show_edit_lrt_modal = false"
    /> -->
    <moderator-modal
      v-if="switch_moderator"
      :participationID="participationID"
      :user_moderator="user_moderator"
      @close="switch_moderator = false"
    />
    <InviteStudents
      v-if="isInviteStudentModalOpen"
      @close="isInviteStudentModalOpen = false"
    />
    <NewRoundTable
      v-if="is_new_roundtable_open"
      :outside_option="outside_option"
      :follow_up_from="getCurrentDiscussion"
      @close="is_new_roundtable_open = false"
    />
    <TemplatesModal
      v-if="templates_modal"
      :activityType="'verbal'"
      @close="closeTemplateModal"
    />
    <SaveSettingsModal
      v-if="is_confirm_settings_modal_open === true"
      @close="is_confirm_settings_modal_open = false"
      @handleSaveLDSettings="saveSettingsTab"
    />
    <MDStartDiscussion 
      v-if="isStartDiscussionModalOpen === true" 
      @close="isStartDiscussionModalOpen = false" 
    />
    <MDEndDiscussion 
      v-if="isEndDiscussionModalOpen === true" 
      @close="isEndDiscussionModalOpen = false" 
    />
    <div v-if="isToShowPolls">
      <Polls v-if="isToShowPolls" :showPage="true" @close="isToShowPolls = false" />
      <NewPoll
        v-if="isToShowPolls"
        :showPage="false"
        @close="isToShowPolls = false"
      />
    </div>
    <LDViewCriteria v-if="isToShowCriteria" @close="isToShowCriteria = false"/>
    <NewPoll
      v-if="!hasTeacherPermissionsInCourse && !isToShowPolls"
      :showPage="false"
      :realTimeVote="true"
    />
    <GuidingQuestions
      v-if="isToShowGuidingQuestions && hasTeacherPermissionsInCourse"
      @close="isToShowGuidingQuestions = false"
    />
    <ChatBox v-if="show_course_chat" 
      @message_notifications_val="handleUpdateUnreadMessages"
      @close="handleChangeChatStatus"
    />
    <LeaveModal 
      v-if="isShowLeaveModal === true" 
      @close="isShowLeaveModal = false" 
      @confirm_accepted="handleConfirmLeave()"
    />
    <GenieRecapQuestionsModal 
      v-if="isFollowUpQuestionsModalOpen === true" 
      @close="isFollowUpQuestionsModalOpen = false" 
      @followUpQuestions="followUpQuestionsCurrent" 
    />
    <DeleteRoundtableModal 
      v-if="show_delete_live_rt_modal === true"
      :roundtable_id="getCurrentDiscussion.id"
      :roundtable_name="getCurrentDiscussion.title" 
      @delete_roundtable="(roundtable_id) => deleteLiveRoundtable(roundtable_id)"
      @close="show_delete_live_rt_modal = false" 
    />
    <ExportEngagementDataModal
      :fileName="exportable_file_name"
      :getFile="fetchClassCSVdata"
      :showButtons="Boolean(getCurrentDiscussion.participations.length)"
      @close="show_export_data_modal = false"
      v-if="show_export_data_modal"
    />
    <RemoveRoundTableSequence 
      v-if="is_to_remove_roundtable_from_sequence === true" 
      @close="is_to_remove_roundtable_from_sequence = false"
      :roundtable_name="getCurrentDiscussion.title"
      sequence_name="Exemplo nome de Sequence"
      @confirm_remove_sequence="() => handleRemoveRoundTableSequence()" 
    />
    <Draft 
      v-if="isFollowUpOpen === true" 
      :id="selectedRoundTableToFollow.draftId" 
      :type="selectedRoundTableToFollow.type"
      :isFollowUp="true" 
      @close="isFollowUpOpen = false"
    />
    <EditVRWordCloud v-if="isToShowEditWordCloud === true" @close="isToShowEditWordCloud = false" :keywordData="keywordData"/>
    <div
      v-if="hasTeacherPermissionsInCourse && showGeniePopover && getCourseDetail.id !== '151035ea-b4c7-484d-a07d-3505fa895cf7'"
      class="fixed bottom-4 z-40 shadow-xl rounded-lg bg-white border border-solid border-gray-200 w-96 transition-all duration-1000 ease-in-out transform"
      :class="{
        'opacity-100 scale-100': showGeniePopover,
        'opacity-0 scale-75': !showGeniePopover
      }"
      style="left: 65px;"
    >
      <div class="flex flex-col space-y-5 px-5 pt-2.5 pb-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-2.5">
            <i class="fa-regular fa-lightbulb text-base text-yellow-500"></i>
            <h6 class="font-poppins font-semibold text-gray-800">Parlay Tip</h6>
          </div>
          <button
            @click="handleCloseGeniePopover()"
            class="w-8 h-8 rounded-full hover:bg-gray-100 hover:text-primary"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="w-full relative">
          <img v-if="genie_teaching_page === 1" src="/static/transcription_teaching1.png" />
          <img v-if="genie_teaching_page === 2" src="/static/transcription_teaching2.png" />
          <img v-if="genie_teaching_page === 3" src="/static/transcription_teaching3.png" />
        </div>
        <div class="flex flex-col space-y-2">
          <h4 v-if="genie_teaching_page === 1 || genie_teaching_page === 3" class="font-poppins font-semibold text-base text-gray-800">
            Have you tried Transcription yet?
          </h4>
          <h4 v-else class="font-poppins font-semibold text-base text-gray-800">
            This makes it easy to remember who said what in the RoundTable.
          </h4>
          <p v-if="genie_teaching_page === 1" class="font-poppins font-light text-gray-600">
            This feature will transcribe each student's speaking turn and then generate a summary, follow up questions and a word cloud!
          </p>
          <p v-else-if="genie_teaching_page === 2" class="font-poppins font-light text-gray-600">
            It will ask for access to each student's microphone when they tap in for the first time.
          </p>
          <p v-else-if="genie_teaching_page === 3" class="font-poppins font-light text-gray-600">
            Turn Transcription <span class="font-semibold text-gray-800">ON</span>
            in your Settings.
          </p>
        </div>
        <div class="flex justify-between items-center">
          <button
            :disabled="genie_teaching_page <= 1"
            @click="handleChangePageTeaching('back')"
            class="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200"
          >
            <i class="fa-solid fa-chevron-left"></i>
          </button>
          <div class="flex items-center space-x-2">
            <div class="w-3 h-3 rounded-full bg-blueGray-500" :class="{'bg-blueGray-800': genie_teaching_page === 1}"></div>
            <div class="w-3 h-3 rounded-full bg-blueGray-500" :class="{'bg-blueGray-800': genie_teaching_page === 2}"></div>
            <div class="w-3 h-3 rounded-full bg-blueGray-500" :class="{'bg-blueGray-800': genie_teaching_page === 3}"></div>
          </div>
          <button
            @click="handleChangePageTeaching('next')"
            :disabled="genie_teaching_page >= 3"
            class="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200"
          >
            <i class="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </DefaultLayout>
</template>
<script>
  import Vue from 'vue';
  import store from 'store';
  /* import moment from 'moment'; */
  import { mapGetters } from 'vuex';
  import LiveDiscussionSummaryPage from 'features/measureLiveDiscussion/classSummaryOverlay';
  import getWelcomeSettings from '../../services/account/getWelcomeSettings';
  import { EventBus } from 'utils/event-bus';
  import AssessmentSettings from './components/editAssessmentPointsLD';
  import generateExportableData from 'services/liveDiscussion/generateExportableData';

  // MIXINS
  import liveDiscussionSocketMixin from 'mixins/sockets/liveDiscussionSocketEvents';
  import courseSocketMixin from 'mixins/sockets/courseSocketEvents';

  import DiscussionCircleNavBar from './components/discussionCircleNavBar';
  import RoundTableWelcomeModal from 'components/modals/roundTableWelcomeModal';

  // COMPONENTS
  import DefaultLayout from 'layouts/default/default.vue';
  import DuplicateRoundtableModal from 'components/modals/duplicateRoundtableModal/duplicateRoundtableModal';
  import integrationsConnectModal from 'components/modals/integrationsConnectModal';
  import googleClassroomModal from 'components/modals/googleClassroomModal';
  import integrationConnectedModal from 'components/modals/shareActivityModalViews/integrationConnectedModal';
  import LiveDiscussionMenu from './components/liveDiscussionMenu';
  import adminServices from 'services/admin/adminServices';
  import urlConfigs from 'services/apiConfigs';
  import googleClassroomService from 'services/googleClassroom/googleClassroomServices';
  import LDPromptTab from './tabs/LDPromptTab';
  import LDSettingsTab from './tabs/LDSettingsTab';
  import LDDiscussionTab from './tabs/LDDiscussionTab';
  import LDSummaryTab from './tabs/LDSummaryTab';
  import LDTranscriptionsTab from './tabs/LDTranscriptionsTab.vue';
  import LDIndividualSummaryTab from './tabs/LDIndividualSummaryTab';
  import createNewPointModal from 'components/createNewPointModal.vue';
  import dialogDeleteConfirm from 'components/dialogDeleteConfirm';
  import ListParticipants from './components/Participants/ListParticipants.vue';
  import ModalSendNudge from './components/Participants/SendNudge.vue';
  import Participant from './components/Participants/Participant.vue';
  import NewFeedbackTemplateModal from '../../components/modals/NewFeedbackModal.vue';
  import ImportFeedbackTemplatesModal from '../../components/modals/ImportFeedbackModal.vue';
  import ModalDeleteSuggestion from './components/Participants/DeleteSuggestionSaved.vue';
  import syncAssessmentDataModal from 'components/modals/syncAssessmentDataModal';
  import connectIntegrationsAssessmentModal from 'components/modals/connectIntegrationsAssessmentModal';
  import editLiveRtModal from 'components/modals/editLiveRtModal';
  import ModeratorModal from './components/modals/moderatorModal.vue';
  import PromptSideBarStudentNotepad from './components/PromptSideBarStudentNotepad';
  import ModalNewSuggestion from './components/Participants/NewSuggestionFeedback.vue';
  import DiscussionQuestionsSecondScreen from './components/discussionQuestionsSecondScreen';
  import TemplatesModal from 'components/modals/templatesModal.vue';
  import MDStartDiscussion from './Modals/MDStartDiscussion.vue';
  import MDEndDiscussion from './Modals/MDEndDiscussion.vue';
  import Polls from 'features/polls_mvp/polls.index.vue';
  import NewPoll from 'features/polls_mvp/polls.modal.vue';
  import GuidingQuestions from 'features/liveDiscussion/components/modals/guidingQuestionsModal';
  import updateUser from 'services/account/updateUser';
  import EditVerbalDiscussion from '../VerbalRoundtables/Dialogs/EditVerbalRoundTable.vue';

  import AgoraRTC from 'agora-rtc-sdk-ng';

  import WelcomeModal from '../dashboard/welcomeModal';
  import ModalTeacherAfterSignUp from '../dashboard/ModalAfterSignUp.vue';

  import toggleOwnAudio from 'services/liveDiscussion/toggleOwnAudio.js';
  import toggleOwnVideo from 'services/liveDiscussion/toggleOwnVideo.js';
  import handleSaveTranscription from 'services/liveDiscussion/handleSaveUserTranscript.js';

  import ListParticipantsCollapsed from './components/Participants/ListParticipantsCollapsed.vue';
  import ChatBox from 'features/courses/components/CourseChatBubble.vue';

  /* import { AcceptedOutsideOptions } from '../../components/modals/New_RoundTable/Defaults'; */
  import axios from 'axios';
  import moment from 'moment';
  import GenieRecapVerbal from './Modals/GenieRecapVerbal.vue';
  import LeaveModal from './Modals/LeaveModal.vue';
  import GenieRecapQuestionsModal from './Modals/FollowUpQuestionsTranscription.vue';
  import Axios from 'axios';
  import DeleteRoundtableModal from '../../components/modals/Delete_Roundtable/DeleteRoundtableModal.vue';
  import ExportEngagementDataModal from '../../components/modals/exportEngagementDataModal.vue';
  /* '../../courses/components/RoundTables/Sequences/RoundTableDessassociateModal.vue' */
  import RemoveRoundTableSequence from '../courses/components/RoundTables/Sequences/RoundTableDessassociateModal.vue';
  import LDViewCriteria from './Modals/LDViewCriteria.vue';
  import EditVRWordCloud from './Modals/EditVRWordCloud.vue';
  import Draft from '../Drafts/Index.vue';
import { WrittenFollowUpTemplate } from '../Sequences/Templates/WrittenFollowUpTemplate';

  export default Vue.extend({
    data() {
      return {
        recognition: null,
        discussion_time: '',
        recognitionNormal: null,
        speechRecognition: false,
        recognitionStart: false,
        recognitionStartNormal: false,
        transcript: "",
        userTranscripts: [],
        speechStartAt: null,
        speechEndAt: null,
        isSpeechStarting: false,
        teacher_wants_to_end_discussion: false,
        show_side_bar: false,
        show_teacher_summary: false,
        show_duplicate_modal: false,
        welcomeRoundTableData: null,
        show_RT_Welcome_modal: false,
        loading_ld: true,
        show_assessment_settings: false,
        open_google_classroom_dialog: false,
        integration: {},
        is_integration_connected: false,
        tab: 'prompt',
        componentKey: 0,
        toogle_participations: true,
        toogle_notepad: true,
        live_discussions_with_point: [],
        selected_point_tag: null,
        selected_point_scale: null,
        show_point_creation_modal: false,
        show_alert_assessment_changing: false,
        delete_point_index: -1,
        show_delete_point_modal: false,
        isOpenSendNudge: false,
        studentInformation: {},
        isOpenParticipant: false,
        show_integrations_modal: false,
        selected_integration: null,
        sync_assessment: false,
        edLink: null,
        logo: null,
        isOpenNewSuggestion: false,
        isOpenImportSuggestions: false,
        isOpenDeleteSuggestion: false,
        selectedSuggestion: {},
        show_edit_lrt_modal: false,
        switch_moderator: false,
        user_moderator: {},
        participationID: '',
        isSaving: false,
        tabToGoAfterSave: '',
        isOpenModalDiscussionStatusChange: false,
        joined_audio_discussion: false,
        show_course_chat: false,
        screenTracks: {
          screenTrack: null,
          screenAudioTrack: null,
        },
        screenRTC: {
          client: null,
        },
        isGenieRecapOpen: true,
        rtc: {
          // For the local client.
          client: null,
          // For the local audio track.
          localAudioTrack: null,
          // For the local video track.
          localVideoTrack: null,
        },
        speaking_volume_level: 3, //above this value is considered speaking
        isFullscreen: false,
        showQuestionIcon: false,
        isModalSampleOpen: false,
        urlVideoSample: '',
        is_button_save_setting_enable: false,
        isInviteStudentModalOpen: false,
        outside_option: '',
        is_new_roundtable_open: false,
        templates_modal: false,
        is_confirm_settings_modal_open: false,
        isStartDiscussionModalOpen: false,
        isEndDiscussionModalOpen: false,
        isToShowPolls: false,
        isToShowCriteria: false,
        isToShowEditWordCloud: false,
        isToShowGuidingQuestions: false,
        message_notifications: 0,
        not_show_heads_up_info: false,
        isShowLeaveModal: false,
        confirmLeave: false,
        isFollowUpQuestionsModalOpen: false,
        followUpQuestionsCurrent: [],
        show_delete_live_rt_modal: false,
        show_export_data_modal: false,
        is_to_remove_roundtable_from_sequence: false,
        keywordData: [],
        genie_teaching_page: 1,
        showGeniePopover: false,
        selectedRoundTableToFollow: null,
        isFollowUpOpen: false,
      };
    },
    components: {
      DefaultLayout,
      Draft,
      EditVerbalDiscussion,
      LDViewCriteria,
      LeaveModal,
      ExportEngagementDataModal,
      LiveDiscussionMenu,
      DiscussionCircleNavBar,
      AssessmentSettings,
      LiveDiscussionSummaryPage,
      DuplicateRoundtableModal,
      RoundTableWelcomeModal,
      integrationsConnectModal,
      googleClassroomModal,
      integrationConnectedModal,
      LDPromptTab,
      LDSettingsTab,
      LDDiscussionTab,
      LDSummaryTab,
      LDIndividualSummaryTab,
      LDTranscriptionsTab,
      createNewPointModal,
      dialogDeleteConfirm,
      ListParticipants,
      ModalSendNudge,
      Participant,
      NewFeedbackTemplateModal,
      ImportFeedbackTemplatesModal,
      ModalDeleteSuggestion,
      ModalNewSuggestion,
      ListParticipantsCollapsed,
      syncAssessmentDataModal,
      connectIntegrationsAssessmentModal,
      ChatBox,
      PromptSideBarStudentNotepad,
      DiscussionSidebarStudentNotes: () =>
        import('./components/discussionSidebarStudentNotes'),
      editLiveRtModal,
      ModeratorModal,
      Tooltip: () => import('../../components/Tooltip.vue'),
      ModalDiscussionIsStarted: () =>
        import('./Modals/NotifyDiscussionStatusChange.vue'),
      ModalSampleRoundTable: () =>
        import('../../components/ModalSampleRoundTable.vue'),
      ModalTeacherAfterSignUp,
      WelcomeModal,
      InviteStudentsModal: () =>
        import('components/modals/InviteStudentsModal.vue'),
      InviteStudents: () =>
        import('../../components/modals/InviteStudents/InviteStudents.vue'),
      DiscussionQuestionsSecondScreen,
      DropdownMenu: () =>
        import(
          '../../features/onlineRoundtables/components/Dropdown/DropdownMenu.vue'
        ),
      NewRoundTable: () =>
        import('components/modals/New_RoundTable/NewRoundTable.vue'),
      TemplatesModal,
      GenieRecapVerbal,
      SaveSettingsModal: () =>
        import('components/modals/SaveSettingsConfirm/SaveSettingsModal.vue'),
        MDStartDiscussion,
        MDEndDiscussion,
        Polls,
        NewPoll,
        GuidingQuestions,
        GenieRecapQuestionsModal,
        DeleteRoundtableModal,
        RemoveRoundTableSequence,
        EditVRWordCloud
    },
    mixins: [liveDiscussionSocketMixin, courseSocketMixin],
    watch: {
      'getCourseDetail.unread_messages': {
        immediate: true,
        handler() {
          this.message_notifications = this.getCourseDetail.unread_messages;
        },
      },
      '$route.params.liveDiscussionID'() {
        const { liveDiscussionID, courseID } = this.$route.params;
        this.setUpLiveDiscussion(liveDiscussionID, courseID);
      },
      recognitionStart: function(newValue, old) {
        if (old === true && newValue === false) {
          if (this.rtc.localAudioTrack.enabled === true) {
            this.recognition.stop();
            this.recognition.start();
          }
        }
      },
      recognitionStartNormal: function(newValue, old) {
        if (old === true && newValue === false) {
          if (this.getCurrentDiscussion.currently_active === this.getProfile.id) {
            this.recognitionNormal.stop();
            this.recognitionNormal.start();
          }
        }
      },
      'getCurrentDiscussion.currently_active'(newValue, old) {
        if (this.recognitionNormal === null) return;

        if (newValue === this.getProfile.id) {
          this.recognitionNormal.stop();
          this.recognitionNormal.start();
        }

        if (newValue !== old && newValue !== this.getProfile.id) {
          this.recognitionNormal.stop();
        }
      },
      'getCurrentDiscussion.teacher_tapped_in_id'(newValue) {
        if (this.hasTeacherPermissionsInCourse === true) {
          if (this.getCurrentDiscussion.teacher_tapped_in_id === this.getProfile.id) {
            this.recognitionNormal.stop();
            this.recognitionNormal.start();
          }
        }

        if (newValue === null) {
          this.recognitionNormal.stop();
        }
      },
      isSaving: function (newValue, old) {
        if (newValue === false && old === true) {
          this.tab = this.tabToGoAfterSave;
          this.$router.push({
            name: 'live-discussion.' + this.tabToGoAfterSave,
          });
        }
      },
      'getCurrentDiscussion.turns_in_circle'() {
        if (
          this.getCurrentDiscussion.audio_discussion_is_enabled &&
          this.joined_audio_discussion
        ) {
          this.createLocalVideoTrack();
        }
      },
      'getCurrentDiscussion.audio_discussion_is_enabled'() {
        if (!this.getCurrentDiscussion.audio_discussion_is_enabled) {
          this.destroyLocalCamera();
          this.leaveCall();
        }
        this.joined_audio_discussion = false;
      },
      'getCurrentDiscussion.is_screen_sharing_active'(newValue) {
        if (newValue === true) {
          this.$store.dispatch('getAgoraRTCConnectionScreen', this.getCurrentDiscussion.id).then(async (options_screen) => {
            if (options_screen) {
              await this.screenRTC.client.join(
                options_screen.app_id,
                options_screen.channel_name,
                options_screen.agora_auth_token,
                options_screen.user_id
              );
            }
          });
        }
        if (newValue === false) {
          EventBus.$emit('reset-view');
        }
      }
    },
    computed: {
      ...mapGetters([
        'hasTeacherPermissionsInCourse',
        'getCurrentDiscussion',
        'getCourseDetail',
        'getCourseLoading',
        'getMyParticipation',
        'getMySubscription',
        'getProfile',
        'getProviders',
        'getStudentSummaryLD',
        'getStudentsInCourse',
      ]),
      discussion_is_live() {
        return this.getCurrentDiscussion.is_live;
      },
      exportable_file_name() {
        const date = moment().format('L'); // MM-DD-YYYY
        const fileName = `${this.getCurrentDiscussion.title} - Live - Export ${date}.csv`;
        return fileName;
      },
      canShowGenieRecap() {
        if (this.tab === 'transcriptions') {
          if (this.isGenieRecapOpen === true) {
            if (this.hasTeacherPermissionsInCourse) {
              if (this.getCurrentDiscussion.transcriptions.length > 0 || this.getCurrentDiscussion.genie_recap !== undefined) {
                return true;
              } else {
                return false;
              }
            } else {
              if(this.getCurrentDiscussion.transcriptions.length > 0 && this.getCurrentDiscussion.genie_recap !== undefined) {
                return true;
              } else {
                return false;
              }
            }
          }
        }
      },
      current_sequence_number: {
        get: function () {
          const position =
            this.getCurrentDiscussion.roundtables_sequence.findIndex(
              (roundtable) =>
                roundtable.id === this.$route.params.liveDiscussionID
            );
          return position;
        },
      },
      setSelectedTabStyle() {
        return 'pb-2 text-primary border-solid font-semibold  border-b-2 border-primary text-sm text-gray-400 cursor-pointer mr-5 border-0';
      },
      setNonSelectedTabStyle() {
        return 'text-sm font-semibold cursor-pointer mr-5 border-0 text-gray-400';
      },
      setGap() {
        if (
          (this.tab === 'discussion' &&
            (this.toogle_participations || this.toogle_notepad)) ||
          (this.tab === 'prompt' && !this.hasTeacherPermissionsInCourse)
        ) {
          return 'lg:gap-3';
        }
        return '';
      },
      setGridSizeLeft() {
        if (this.tab === 'discussion') {
          return this.toogle_participations || this.toogle_notepad
            ? 'w-full lg:w-8/12 xl:w-9/12 2xl:w-9/12'
            : 'w-full';
        } else if (this.tab === 'prompt')
          return this.hasTeacherPermissionsInCourse
            ? 'w-full'
            : 'w-full lg:w-1/2';
        else return 'w-full';
      },
      setGridSizeRight() {
        if (this.tab === 'discussion') {
          return this.toogle_participations || this.toogle_notepad
            ? 'mt-3 lg:mt-0 w-full lg:w-4/12 xl:w-3/12 2xl:w-3/12'
            : 'border-none';
        } else if (this.tab === 'prompt')
          return this.hasTeacherPermissionsInCourse
            ? 'border-none'
            : 'mt-3 lg:mt-0 w-full lg:w-1/2';
        else return 'border-none';
      },
      rt_integration() {
        const google_integration =
          this.getCurrentDiscussion.google_classroom_course_work_id;
        if (google_integration) {
          return this.getCurrentDiscussion.google_classroom_course_work_url;
        }
        const integrations = this.getCurrentDiscussion.edlink_integrations;
        if (integrations && integrations.length) {
          return integrations[0];
        }
      },
      myParticipation: function () {
        return this.getMyParticipation.live_discussion_id;
      },
      assignmentUrl() {
        const google_integration =
          this.getCurrentDiscussion.google_classroom_course_work_id;
        if (google_integration) {
          return this.getCurrentDiscussion.google_classroom_course_work_url;
        }
        return this.rt_integration ? this.rt_integration.url : null;
      },
      assignmentIcon() {
        if (this.rt_integration) {
          const name = this.rt_integration.provider_name || 'Google Classroom';
          if (name === 'Google Classroom')
            return '/static/google_class_round.png';

          if (this.rt_integration.provider_name === 'Schoology')
            return '/static/integrations/Schoology.png';

          if (this.rt_integration.provider_name === 'Canvas')
            return '/static/integrations/Canvas.png';

          if (this.rt_integration.provider_name === 'Microsoft')
            return '/static/integrations/Microsoft.png';

          if (this.rt_integration.provider_name === 'Brightspace')
            return '/static/integrations/Brightspace.png';

          const provider = this.getProviders.find(
            (p) =>
              p.name.toLowerCase() ===
              this.rt_integration.provider_name.toLowerCase()
          );
          return provider ? provider.icon_url : null;
        }
      },
      component_to_render() {
        const tab = this.tab.charAt(0).toUpperCase() + this.tab.slice(1);
        return 'LD' + tab + 'Tab';
      },
      co_teachers() {
        return this.getCurrentDiscussion.co_teachers;
      },
      my_participation() {
        return this.getCurrentDiscussion.participations.find(
          (p) => p.user_id === this.getProfile.id
        );
      },
      course_author() {
        const author = this.getCourseDetail.users.find(
          (user) =>
            user.subscription.role === 'author' &&
            user.id !== this.getCurrentDiscussion.author_id
        );
        return author ? author : '{}';
      },
      second_screen() {
        return this.$route.query.second_screen;
      },
    },
    methods: {
      verbalDiscussionDuration() {
        const time_now = new Date();
        const start_LD_time = new Date(this.getCurrentDiscussion.discussion_start_time);
        const db_discussion_time = Number(this.getCurrentDiscussion.discussion_time);

        const discussion_time = db_discussion_time + moment(time_now - start_LD_time).valueOf();

        this.discussion_time = moment.utc(Number(discussion_time)).format('HH:mm:ss');
      },
      handleCloseGeniePopover: async function() {
        try {
          this.showGeniePopover = !this.showGeniePopover;
          const { data } = await updateUser(this.getProfile.id, { transcription_feature_flag: true });
          await this.$store.dispatch('setAccount', data);
        } catch (error) {
          console.log(error);
        }
      },
      handleChangePageTeaching: function(page) {
        if (page === "back" && this.genie_teaching_page === 1) {
          return;
        }
        if (page === "next" && this.genie_teaching_page > 3) {
          return;
        }
        if (page === "next") {
          this.genie_teaching_page = this.genie_teaching_page + 1;
        } else if(page === "back") {
          this.genie_teaching_page = this.genie_teaching_page - 1;
        }
      },
      handleUpdateRoundTables: async function() {
        this.getCurrentDiscussion.roundtables_sequence.map(async (roundtable) => {
          if (roundtable.type === 'roundtable' || roundtable.type === 'written') {
            await this.$store.dispatch('savingRTDetails', {
              id: roundtable.id,
              sequence_id: null,
              sequences_id: null,
            });
          }
          if (roundtable.type === 'live_discussion' || roundtable.type === 'verbal') {
            await this.$store.dispatch('saveLiveDiscussionSettings', {
              id: roundtable.id,
              sequence_id: null,
            });
          }
        });
      },
      handleRemoveRoundTableSequence: async function() {
        const sequence_id = this.getCurrentDiscussion.sequence_id;
        if (sequence_id === null) {
          return;
        }
        try {
          if (this.getCurrentDiscussion.roundtables_sequence.length === 2) {
            await this.handleUpdateRoundTables();
            this.is_to_remove_roundtable_from_sequence = false;
            await this.$store.dispatch('fetchCourseDetails',
              this.$route.params.courseID
            );
            this.$router.push({
              name: 'course.index',
              courseID: this.getCourseDetail.id,
            });
            return;
          }
          await this.$store.dispatch('saveLiveDiscussionSettings', {
            id: this.getCurrentDiscussion.id,
            sequence_id: null,
          });
          this.is_to_remove_roundtable_from_sequence = false;
          await this.$store.dispatch(
            'fetchCourseDetails',
            this.$route.params.courseID
          );
          this.$router.push({
            name: 'course.index',
            courseID: this.getCourseDetail.id,
          });
          return;
        } catch (error) {
          return console.log(error);
        }
      },
      fetchClassCSVdata() {
        // Make request for data here
        return generateExportableData(this.getCurrentDiscussion.id).then(
          (res) => res.data
        );
      },
      deleteLiveRoundtable: async function() {
        if (this.getCurrentDiscussion.roundtables_sequence.length === 2) {
          await this.handleUpdateRoundTables();
        }
        await this.$store.dispatch('deleteDiscussion', { id: this.getCurrentDiscussion.id, rtPage: true });
      },
      handleExecuteNormalTranscription: async function() {
        if (this.getCurrentDiscussion.audio_discussion_is_enabled === false && this.getCurrentDiscussion.is_transcription_enable === true) {
          const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
          const recognition =  new Recognition();
          this.recognitionNormal = recognition;
          this.recognitionNormal.continuous = true;
          this.recognitionNormal.lang = "en-US";
        }
      },
      handleExecuteTranscription: async function() {
        if (this.getCurrentDiscussion.audio_discussion_is_enabled === true && this.getCurrentDiscussion.is_transcription_enable === true) {
          const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
          const recognition =  new Recognition();
          this.recognition = recognition;
          this.recognition.continuous = true;
          this.recognition.lang = "en-US";
        }
      },
      handleGetDiscussionTranscription: async function(discussion_id) {
        try {
          const transcription = await Axios.get(`/api/transcriptions/${discussion_id}/transcription`);
          const transcription_data = transcription.data.data;
          this.$store.commit("SET_VERBAL_TRANSCRIPTION", transcription_data);
        } catch (error) { 
          console.log(error);
        }
      },
      handleStartFollowUp: async function(roundtable_type) {
        const questions = [];
        this.getCurrentDiscussion.genie_recap.questions.map((q, index) => {
          questions.push({
            id: index,
            question: q.question,
            is_custom: true,
            is_active: true,
          });
        });
        const questionsHtml = this.getCurrentDiscussion.genie_recap.questions.map((q, i) => `<li id="${i}">${q.question}</li>`).join('');
        const payload = {
          title: `${this.getCurrentDiscussion.title} - Follow Up (${this.getCurrentDiscussion.roundtables_sequence.length === 0 ? 1 : this.getCurrentDiscussion.roundtables_sequence.length})`,
          prompt: `
            <div>
              <div id="discussion-summary">
                <h2>Summary</h2>
                <p>${this.getCurrentDiscussion.genie_recap.prompt_summary}</p>
              </div>
              <div id="discussion-questions">
                <h2>Discussion Questions</h2>
                <p>Answer the following questions as a follow-up to our original RoundTable discussion</p>
                <ol id="questions">
                  ${questionsHtml}
                </ol>
              </div>
              <div id="discussion-instructions">
                <h2>Peer Feedback Instructions</h2>
                <p>After submitting your response, read at least two of your classmates’ responses and post a reply.</p>
                <ul>
                  <li>Did they change your perspective in any way?</li>
                  <li>Do you agree or disagree? Why or why not?</li>
                  <li>Can you add to their ideas?</li>
                </ul>
              </div>
            </div>  
          `,
          type: roundtable_type,
          instructions: this.getCurrentDiscussion.genie_recap.prompt_summary,
          user_id: this.getProfile.id,
          courses: [this.getCurrentDiscussion.course_id],
          is_follow_up: true,
          follow_up_from_id: this.getCurrentDiscussion.id,
          follow_up_from_type: this.getCurrentDiscussion.type,
          toggleable_guiding_questions: questions,
          is_generated: true
        };

        if (!this.getProfile.is_paid && this.getProfile.activity_passes === 0) {
          const toast = { type: 'Info', message: 'Sorry, you have no free RoundTables left! Delete an old RoundTable or upgrade your account!', time: 5000,upgrade: true };
          return EventBus.$emit('createToast', toast);
        }

        const draft = await Axios.post(`/api/drafts/draft`, payload);
        if (!draft) {
          throw new Error('Something happened starting roundtable creation! Please try again');
        }
        await this.$store.dispatch('useActivityPass');
        this.selectedRoundTableToFollow = { 
          id: this.getCurrentDiscussion.id,
          draftId: draft.data.id,
          currentRoundtableType: this.getCurrentDiscussion.type, 
          type: roundtable_type, 
          course: this.getCourseDetail,
        };
        this.isFollowUpOpen = true;
      },
      studentsWithLastName: function (students_list) {
        let students = [];
        students_list.forEach((item) => {
          const lastName = item.name.split(' ').slice(-1).join(' ');
          item.lastName = lastName;
          students.push(item);
        });
        return students;
      },
      handleConfirmLeave: function() {
        this.confirmLeave = true;
        if (this.hasTeacherPermissionsInCourse) {
          if (!this.getProfile.is_paid) {
            const text = 'student reports and data export feature for all your classes.';
            const studentReport = true;
            EventBus.$emit('open-upgrade-modal', text, studentReport);
            return;
          }
          const students = this.studentsWithLastName(this.getStudentsInCourse);

          students.sort((a, b) => a.lastName.localeCompare(b.lastName));

          return this.$router.push({
            name: 'course.student-reports.summary',
            params: { studentID: students[0].id },
          });
        }
        return this.$router.push({
          name: 'course.student-reports.summary',
          params: { studentID: this.getProfile.id },
        });
      },
      stopAndCloseTracks: function (tracks) {
        ['screenTrack', 'screenAudioTrack', 'localAudioTrack', 'localVideoTrack'].forEach(trackName => {
          if (tracks[trackName] !== null) {
            tracks[trackName].stop();
            tracks[trackName].close();
            tracks[trackName] = null;
          }
        });
      },
      handleUpdateUnreadMessages: function (val) {
        this.message_notifications = val;
      },
      handleChangeChatStatus: function () {
        this.show_course_chat = !this.show_course_chat;
        if (this.show_course_chat) {
          this.$store.dispatch('readCourseMessages', {
            courseID: this.getCourseDetail.id,
          });
        }
      },
      handleClickSequenceRoundTable: async function (
        roundtable_sequence_id,
        roundtable_sequence_type
      ) {
        if (
          roundtable_sequence_type === 'written' ||
          roundtable_sequence_type === 'roundtable'
        ) {
          const route = {
            name: 'roundtable.index',
            params: {
              roundtableID: roundtable_sequence_id,
              courseID: this.$route.params.courseID,
            },
          };
          this.$router.push(route);
          this.$store.commit('setIsReloadComponent');
          return;
        }
        if (
          roundtable_sequence_type === 'verbal' ||
          roundtable_sequence_type === 'live_discussion'
        ) {

          
          const route = {
            name: 'live-discussion.index',
            params: {
              liveDiscussionID: roundtable_sequence_id,
              courseID: this.$route.params.courseID,
            },
          };
          this.$router.push(route);
          this.$store.commit('setIsReloadComponent');
          return;
        }
      },
      toggleFullscreen() {
        if (this.hasTeacherPermissionsInCourse) {
          this.toogle_participations = true;
          EventBus.$emit('resize-section', this.toogle_participations);
          this.isFullscreen = !this.isFullscreen;
        } else {
          this.toogle_notepad = true;
          EventBus.$emit('resize-section', this.toogle_notepad);
          this.isFullscreen = !this.isFullscreen;
        }
      },
      goToTab(tab) {
        const route_name = 'live-discussion.' + tab;
        this.tabToGoAfterSave = tab;
        if (this.isSaving === false) {
          this.tab = tab;
          this.$router.push({
            name: route_name,
            query: this.$route.query !== null ? this.$route.query : '',
          });
        }
      },
      showWelcomeVideosOnSamples: function () {
        if (this.$route.params.courseID === this.$store.state.course_sample_id) {
          /* ID of Course Sample and Roundtable Sample */
          if (
            this.$route.name === 'live-discussion.index' &&
            this.$route.params.liveDiscussionID ===
              '8ef8c9e2-b07d-45b0-ac58-991a3416e146'
          ) {
            getWelcomeSettings
              .getSettingsByQuery('sample_verbal')
              .then((res) => {
                this.isModalSampleOpen = true;
                this.urlVideoSample = res[0];
              });
          }
        }
      },
      forceRerender() {
        this.componentKey += 1;
      },
      async loadPage(liveDiscussionID, courseID) {
        const payload_to_localStorage = {
          type: 'live',
          id: liveDiscussionID,
        };
        localStorage.setItem(
          `${courseID}_last_accessed`,
          JSON.stringify(payload_to_localStorage)
        );
        await store.dispatch('enterLiveDiscussion', {
          liveDiscussionID,
          courseID,
        });
        this.setupLiveDiscussionSocket(liveDiscussionID);
        this.setupCourseSocket(courseID);
        this.loading_ld = false;
      },
      openAssessmentSettings() {
        this.show_assessment_settings = true;
        EventBus.$emit('showAssessmentSettings', this.show_assessment_settings);
      },
      openSendNudgeModal: function (isOpen, studentInformation) {
        this.isOpenSendNudge = isOpen;
        this.studentInformation = studentInformation;
      },
      closeSendNudgeModal: function (toClose) {
        this.isOpenSendNudge = toClose;
      },
      openParticipant: function (isOpen, studentInformation) {
        this.studentInformation = studentInformation;
        if (this.getStudentSummaryLD.user_id === studentInformation.user_id) {
          this.isOpenParticipant = isOpen;
          return;
        }
        store
          .dispatch('fetchParticipationSummary', studentInformation.id)
          .then((data) => (this.studentInformation = data))
          .finally(() => (this.isOpenParticipant = isOpen));
      },
      openNewSuggestion: function (isOpen, suggestionToEdit) {
        if (!this.getProfile.is_paid) {
          const text =
            'save feedback template feature for both types of RoundTables.';
          EventBus.$emit('open-upgrade-modal', text);
          return;
        }
        this.isOpenNewSuggestion = isOpen;
        this.selectedSuggestion = suggestionToEdit;
      },
      openImportSuggestions: function (isOpen) {
        if (!this.getProfile.is_paid) {
          const text =
            'save feedback template feature for both types of RoundTables.';
          EventBus.$emit('open-upgrade-modal', text);
          return;
        }
        this.isOpenImportSuggestions = isOpen;
      },
      openConfirmDeleteSuggestion: function (isOpen, suggestionToDelete) {
        this.isOpenDeleteSuggestion = isOpen;
        this.selectedSuggestion = suggestionToDelete;
      },
      /**
       * show modal when clicking the button
       * not dependant on the admin portal configuration
       * but needs video urls to show modal
       */
      showRTWelcomeModal() {
        getWelcomeSettings.getSettingsByQuery('live').then((data) => {
          let videos = data.filter(
            (e) => e.video_url && e.video_url.length > 0
          );
          this.welcomeRoundTableData = data;
          if (videos.length > 0) {
            this.show_RT_Welcome_modal = true;
          }
        });
      },
      closeRTWelcomeModal() {
        this.show_RT_Welcome_modal = false;
      },
      stepsToLeaveRoute(to, from, next) {
        if (to.params.liveDiscussionID !== from.params.liveDiscussionID) {
          this.leaveLiveDiscussionSocket(from.params.liveDiscussionID);
        }
        next();
      },
      async submitIntegrationSubmission() {
        const { edlink_integrations: course_edlink_integrations } =
          this.getCourseDetail;

        const url = `${urlConfigs.uiURL}/classes/${this.getCourseDetail.id}/roundtables/live/${this.getCurrentDiscussion.id}/?studentID=${this.getMyParticipation.id}&tab=Feedback`;

        const payload = {
          body: `<div style="margin-left:20px ">Click the link below to see this student submission inside of Parlay:</div>
            <br>
            <a href="${url}"
            <span style="margin-left: 20px;font-weight: bold;"> Parlay Submission </span>
            </a>
            </div>`,
          url, // it's necessary for canvas integration
          type: 'text', // it's necessary for canvas integration
        };
        //only one has of right now
        const { edlink_integrations } = this.getCurrentDiscussion;
        sessionStorage.setItem('course_id', this.getCourseDetail.id);

        sessionStorage.setItem('activity_id', this.getCurrentDiscussion.id);
        sessionStorage.setItem('activity_type', 'live');
        const course_id = this.getCourseDetail.id;

        for (let j = 0; j < edlink_integrations.length; j += 1) {
          const integration = edlink_integrations[j];
          const course_integration = course_edlink_integrations.find(
            (x) => x.id === integration.id
          );
          const { course_id: edlink_course_id } = course_integration;
          const user_integration = this.getProfile.edlink_integrations.find(
            (x) => x.id === integration.id
          );
          const { id: integration_id, assignment_id } = integration;
          const submission_integration =
            this.getMyParticipation.edlink_integrations.find(
              (x) => x.id === integration.id
            );
          // check if account is connect
          // if submission was created from integration
          if (submission_integration) {
            if (!user_integration) {
              let route = {};
              route = {
                name: 'student.settings',
                params: {
                  courseID: this.$route.params.courseID,
                },
                query: {
                  discussionID: this.$route.params.liveDiscussionID,
                },
              };
              this.$router.push(route);
            } else {
              const submission_integration =
                this.getMyParticipation.edlink_integrations.find(
                  (x) => x.id === integration.id
                );
              if (!submission_integration.submission_id) {
                await adminServices.createSsoCourseAssignmentSubmission(
                  course_id,
                  integration_id,
                  edlink_course_id,
                  'verbal',
                  this.getCurrentDiscussion.id,
                  assignment_id,
                  payload
                );
              }
            }
          }
        }
      },
      connectGoogleAccount() {
        sessionStorage.setItem('course_id', this.getCourseDetail.id);
        sessionStorage.setItem('activity_id', this.getCurrentDiscussion.id);
        sessionStorage.setItem('activity_type', 'live');
        let route = {};
        route = {
          name: 'student.settings',
          params: {
            courseID: this.getCourseDetail.id,
          },
          query: {
            discussionID: this.getCurrentDiscussion.id,
          },
        };
        this.$router.push(route);
      },
      async submitGoogleClassroomSubmission() {
        const link = `${urlConfigs.uiURL}/classes/${this.getCourseDetail.id}/roundtables/live/${this.getCurrentDiscussion.id}/?studentID=${this.getMyParticipation.id}&tab=Feedback`;

        const payload = {
          link,
        };

        await googleClassroomService.createGoogleClassroomAssignmentSubmission(
          this.getCourseDetail.id,
          this.getCourseDetail.google_classroom_integration.course_id,
          'verbal',
          this.getCurrentDiscussion.id,
          this.getCurrentDiscussion.google_classroom_course_work_id,
          payload
        );
      },
      async setUpLiveDiscussion(liveDiscussionID, courseID) {
        await this.loadPage(liveDiscussionID, courseID);
      },
      checkRoundtablesWithPoint(tag) {
        this.selected_point_tag = tag;
        const { roundtables } = this.getCourseDetail;
        let result = [];
        for (let index = 0; index < roundtables.length; index += 1) {
          const ld = roundtables[index];
          const { peer_rewards } = ld.points_settings;
          const has_point = peer_rewards.find(
            (p) => p.tag === this.selected_point_tag
          );
          if (has_point) {
            result.push({ title: ld.title, id: ld.id });
          }
        }
        this.live_discussions_with_point = result;
        if (this.live_discussions_with_point.length && this.getProfile.is_criteria_editing_message_disabled === false) {
          this.show_alert_assessment_changing = true;
        }
        return result;
      },
      editAssessmentPoint(value, tag, point_scale) {
        this.activityType = value;
        this.selected_point_scale = point_scale;
        if (tag) {
          this.checkRoundtablesWithPoint(tag);
          this.selected_point_tag = tag;
        }
        if (this.live_discussions_with_point.length === 0 || this.getProfile.is_criteria_editing_message_disabled === true) {
          this.show_point_creation_modal = true;
        }
      },
      deleteAssessmentPoint(tag) {
        this.delete_point_index =this.getCourseDetail.points_settings.peer_rewards.findIndex((x) => x.tag == tag);
        this.checkRoundtablesWithPoint(tag);
        if (this.live_discussions_with_point.length === 0 || this.getProfile.is_criteria_editing_message_disabled === true) {
          this.show_delete_point_modal = true;
        }
      },
      closeDeletePointModal() {
        this.delete_point_index = -1;
        this.show_delete_point_modal = false;
      },
      async confirmDeleteAssessmentPoint() {
        let clonedCoursePointSettings =
          this.getCourseDetail.points_settings.peer_rewards.slice();
        clonedCoursePointSettings.splice(this.delete_point_index, 1);
        let payload = [];
        if (this.live_discussions_with_point.length) {
          for (let i = 0; i < this.live_discussions_with_point.length; i += 1) {
            const ld = this.getCourseDetail.live_discussions.find(
              (r) => r.id === this.live_discussions_with_point[i].id
            );
            const index_ld = ld.points_settings.peer_rewards.findIndex(
              (x) => x.tag === this.selected_point_tag
            );
            const peer_rewards = ld.points_settings.peer_rewards.slice();
            peer_rewards.splice(index_ld, 1);
            payload.push({ id: ld.id, peer_rewards });
          }
        }
        this.delete_point_index = -1;
        if (payload.length) {
          await this.$store.dispatch('updateRTsCriteria', {
            id: this.getCourseDetail.id,
            criterias: payload,
            activityType: 'verbal',
          });
          const currentRTChanged = payload.find(
            (x) => x.id === this.getCurrentRT.id
          );
          if (currentRTChanged) {
            const { peer_rewards } = currentRTChanged;
            const cloned_rt = Object.assign({}, this.getCurrentRT);
            cloned_rt.points_settings = { peer_rewards };
            this.$store.commit('UPDATE_RT_DETAILS', cloned_rt);
          }
        }
        this.$store
          .dispatch('updateCourseDetail', {
            id: this.getCourseDetail.id,
            points_settings: { peer_rewards: clonedCoursePointSettings },
          })
          .then(() => {
            this.show_delete_point_modal = false;
          });
      },
      closeAssessmentChangingAlert: async function() {
        if (this.not_show_heads_up_info === true) {
          await updateUser(this.getProfile.id, { is_criteria_editing_message_disabled: true });
        }
        this.show_alert_assessment_changing = false;
        if (this.delete_point_index !== -1) {
          this.show_delete_point_modal = true;
        } else {
          this.show_point_creation_modal = true;
        }
      },
      setAsModerator(participation) {
        this.participationID = participation.id;
        const user = this.getCurrentDiscussion.participations.find(
          (p) => p.id === participation.id
        );
        const user_moderator = this.getCurrentDiscussion.participations.find(
          (p) => p.is_moderator
        );

        if (user_moderator && user_moderator.id !== participation.id) {
          this.user_moderator = user_moderator;
          this.switch_moderator = true;
        } else if (user.is_moderator) {
          this.$store
            .dispatch('resetModerator', this.$route.params.liveDiscussionID)
            .then(() => {
              const toast = {
                type: 'Success',
                message: 'User moderator removed with success',
                time: 5000,
              };
              EventBus.$emit('createToast', toast);
            });
        } else {
          this.$store
            .dispatch('moderatorChosen', {
              live_discussion_id: this.$route.params.liveDiscussionID,
              user_id: user.user_id,
            })
            .then(() => {
              const toast = {
                type: 'Success',
                message: 'User moderator updated with success',
                time: 5000,
              };
              EventBus.$emit('createToast', toast);
            });
        }
      },
      startRecognition() {
        this.transcript = "";
        this.recognition.start();
      },
      stopRecognition() {
        this.recognition.stop();
        delete this.recognition;
      },
      sendTalkingParticipants(participants) {
        const participations_list = {};
        const teachers_list = {};
        const co_teachers_list = {};

        participants.forEach((volume) => {
          const ld_author = volume.uid === this.getCurrentDiscussion.author_id;
          const course_author = volume.uid === this.course_author ? this.course_author.id : false;
          const is_speaking = volume.level > this.speaking_volume_level;
          if (ld_author || course_author) {
            teachers_list[volume.uid] = is_speaking;
          }
          if (this.co_teachers && this.co_teachers.length) {
            co_teachers_list[volume.uid] = is_speaking;
          }
          participations_list[volume.uid] = is_speaking;
        });

        EventBus.$emit('teacher_speaking', teachers_list);
        EventBus.$emit('co_teachers_speaking', co_teachers_list);
        EventBus.$emit('participations_speaking', participations_list);
      },
      async initializeRtcClientEvents() {
        
        this.rtc.client.on('user-published', async (user, mediaType) => {
          await this.rtc.client.subscribe(user, mediaType);

          if (mediaType === 'video') {
            const { videoTrack: remoteVideoTrack } = user;
            const remotePlayerContainer = document.createElement('div');

            remotePlayerContainer.id = user.uid.toString();
            remotePlayerContainer.style.top = '0';
            remotePlayerContainer.style.height = '100%';
            remotePlayerContainer.style.width = '100%';
            remotePlayerContainer.style.position = 'absolute';
            remotePlayerContainer.style.borderRadius = '0 0 4px 4px';

            const discussion_circle = document.getElementById(`circle-list-${user.uid.toString()}`);
            if (discussion_circle) {
              discussion_circle.appendChild(remotePlayerContainer);
              remoteVideoTrack.play(remotePlayerContainer);

              const trackID = remoteVideoTrack.getTrackId();
              const elementVideo = document.querySelector(`[id^="agora-video-player-${trackID}"]`);
              elementVideo.style.borderRadius = '0 0 4px 4px';
            }
          }

          if (mediaType === 'audio') {
            const { audioTrack: remoteAudioTrack } = user;
            remoteAudioTrack.play();
          }
        });

        this.rtc.client.on('volume-indicator', (result) => {
          this.sendTalkingParticipants(result);
        });

        this.rtc.client.on('network-quality', (stats) => {
          if (stats.downlinkNetworkQuality > 4 || stats.uplinkNetworkQuality > 4) {
            EventBus.$emit('bad-network-quality', true);
          } else {
            EventBus.$emit('bad-network-quality', false);
          }
        });

        this.rtc.client.on('token-privilege-will-expire', async () => {
          if (this.getCurrentDiscussion.audio_discussion_is_enabled) {
            const options = await this.$store.dispatch('getAgoraRTCConnection', this.getCurrentDiscussion.id);

            if (!options.show_stopped_modal) {
              await this.rtc.client.renewToken(options.agora_auth_token);
            }
          }
        });

        this.rtc.client.on('token-privilege-did-expire', async () => {
          if (this.getCurrentDiscussion.audio_discussion_is_enabled) {
            const options = await this.$store.dispatch('getAgoraRTCConnection', this.getCurrentDiscussion.id);
            if (!options.show_stopped_modal) {
              await this.rtc.client.renewToken(options.agora_auth_token);
            }
          }
        });

        this.screenRTC.client.on('user-published', async (user, mediaType) => {
          await this.screenRTC.client.subscribe(user, mediaType);
          if (mediaType === 'video') {
            const { videoTrack: remoteVideoTrack } = user;
            const container = document.getElementById('screen-share-container');
            remoteVideoTrack.play(container);
          }
          if (mediaType === 'audio') {
            const { audioTrack: remoteAudioTrack } = user;
            remoteAudioTrack.play();
          }
        });

        this.screenRTC.client.on('user-unpublished', async (user) => {
          await this.screenRTC.client.unsubscribe(user);
          const playerContainer = document.getElementById(user.uid);
          EventBus.$emit('reset-view');
          await this.screenRTC.client.leave();
          if (playerContainer) {
            playerContainer.remove();
          }
        });

        this.screenRTC.client.on('token-privilege-will-expire', async () => {
          if (this.getCurrentDiscussion.is_screen_sharing_active && this.getCurrentDiscussion.audio_discussion_is_enabled) {
            const options_screen = await this.$store.dispatch('getAgoraRTCConnectionScreen', this.getCurrentDiscussion.id);
            if (!options_screen.show_stopped_modal) {
              await this.screenRTC.client.renewToken(options_screen.agora_auth_token);
            }
          }
        });

        this.screenRTC.client.on('token-privilege-did-expire', async () => {
          if (this.getCurrentDiscussion.is_screen_sharing_active && this.getCurrentDiscussion.audio_discussion_is_enabled) {
            const options_screen = await this.$store.dispatch('getAgoraRTCConnectionScreen', this.getCurrentDiscussion.id);
            if (!options_screen.show_stopped_modal) {
              await this.screenRTC.client.renewToken(options_screen.agora_auth_token);
            }
          }
        });
      },
      startCall: async function() {
        try {
          this.screenRTC.client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp9' });
          this.rtc.client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp9' });
          
          if (this.rtc.client) {

            this.rtc.client.enableAudioVolumeIndicator();
            await this.initializeRtcClientEvents();

            const options = await this.$store.dispatch('getAgoraRTCConnection', this.getCurrentDiscussion.id);
            
            if (options) {
              const userId = await this.rtc.client.join(options.app_id, options.channel_name, options.agora_auth_token, options.user_id);

              if (userId !== options.user_id) {
                throw new Error('Resulting user ID is not the same as the specified one.');
              }

              const audioDevice = localStorage.getItem('preferredAudioDevice');
              if (audioDevice) {
                this.rtc.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({ microphoneId: audioDevice });
              } else {
                this.rtc.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
              }
              const videoDevice = localStorage.getItem('preferredVideoDevice');

              const videoOptions = {
                encoderConfig: {
                  resolution: '480p',
                  frameRate: 30,
                  bitrateMax: 1000,
                }
              };

              if (videoDevice) {
                this.rtc.localVideoTrack = await AgoraRTC.createCameraVideoTrack({ cameraId: videoDevice, ...videoOptions, optimizationMode: "motion" });
              } else {
                this.rtc.localVideoTrack = await AgoraRTC.createCameraVideoTrack({ ...videoOptions, optimizationMode: "motion"});
              }
              
              if (!this.rtc.localAudioTrack || !this.rtc.localVideoTrack) {
                throw new Error('An error occurred while creating the local audio/video track.');
              } else {
                await this.rtc.client.publish([this.rtc.localAudioTrack, this.rtc.localVideoTrack]);
              }
            }
          }
        } catch (error) {
          this.joined_audio_discussion = false;
        }
      },
      handleStartScreenShare: async function() {

        if (this.getCurrentDiscussion.is_screen_sharing_active === true) {
          return;
        }

        const payload = {
          live_discussion_id: this.getCurrentDiscussion.id
        };
        
        await axios.post('/api/live_discussions/start-share-screen', payload).then(async () => {

          const tracks = await  AgoraRTC.createScreenVideoTrack({encoderConfig: "720p_2", optimizationMode: "motion" }, "auto");

          const container = document.getElementById('screen-share-container');

          if (tracks instanceof Array) {
            this.screenTracks.screenTrack = tracks[0];
            this.screenTracks.screenAudioTrack = tracks[1];

            this.screenTracks.screenTrack.play(container);
            await this.screenRTC.client.publish([this.screenTracks.screenTrack, this.screenTracks.screenAudioTrack]);
            
          } else {
            this.screenTracks.screenTrack = tracks;
            this.screenTracks.screenTrack.play(container);
            await this.screenRTC.client.publish([this.screenTracks.screenTrack]);
          }

        });
      },
      handleStopShareScreen: async function() {
        if (this.getCurrentDiscussion.is_screen_sharing_active === false) return;
        await axios.put(`/api/live_discussions/${this.getCurrentDiscussion.id}/stop-share-screen`).then(async () => {
          await this.screenRTC.client.unpublish([this.screenTracks.screenTrack]);
          this.screenTracks.screenTrack.close();
          this.screenTracks.screenTrack.stop();
          this.screenRTC.client.leave();
        });
      },
      startLiveCall: async function() {
        await this.startCall();

        this.joined_audio_discussion = true;

        if (this.rtc.localAudioTrack) {
          await this.muteAudio();
        }
        if (this.rtc.localVideoTrack) {
          await this.videoOn();
        }

        const options_screen = await this.$store.dispatch('getAgoraRTCConnectionScreen', this.getCurrentDiscussion.id);
        if (options_screen) {
          await this.screenRTC.client.join(options_screen.app_id, options_screen.channel_name, options_screen.agora_auth_token, options_screen.user_id);
        }
      },
      muteAudio: async function() {
        await this.rtc.localAudioTrack.setEnabled(false);
        if (this.getCurrentDiscussion.is_transcription_enable === true && this.recognition !== null) {
          this.recognition.stop();
          EventBus.$emit('capturing-transcription', false);
        }
      },
      unmuteAudio: async function() {
        await this.rtc.localAudioTrack.setEnabled(true);

        if (this.getCurrentDiscussion.is_transcription_enable === true && this.recognition !== null) {
          this.recognition.start();
        }
      },
      videoOff: async function() {
        const trackID = this.rtc.localVideoTrack.getTrackId();
        const elementVideo = document.querySelector(`[id^="agora-video-player-${trackID}"]`);
        if (elementVideo) {
          elementVideo.style.opacity = 0;
        }
        await this.rtc.localVideoTrack.setEnabled(false);
      },
      videoOn: async function() {
        await this.rtc.localVideoTrack.setEnabled(false);
        const trackID = this.rtc.localVideoTrack.getTrackId();
        const elementVideo = document.querySelector(`[id^="agora-video-player-${trackID}"]`);
        if (elementVideo) {
          elementVideo.style.opacity = 100;
        }
        await this.rtc.localVideoTrack.setEnabled(true);
      },
      leaveCall: async function() {
        // Destroy the local audio and track.
        if (this.rtc.localAudioTrack) {
          this.rtc.localAudioTrack.close();
        }
        if (this.rtc.localVideoTrack) {
          this.rtc.localVideoTrack.close();
        }
        this.sendTalkingParticipants([]);
        // Leave the channel.
        if (this.rtc.client) {
          // Traverse all remote users.
          this.rtc.client.remoteUsers.forEach((user) => {
            // Destroy the dynamically created DIV containers.
            const playerContainer = document.getElementById(user.uid);
            playerContainer && playerContainer.remove();
          });
          await this.rtc.client.leave();
        }
      },

      showRTWelcomeModalQuestionIcon() {
        getWelcomeSettings
          .getSettingsByQuery('live')
          .then((welcomeSettings) => {
            let settings;
            // If the user is a teacher.
            if (this.hasTeacherPermissionsInCourse) {
              settings = welcomeSettings.find((s) => s.role === 'teacher');
            } else {
              settings = welcomeSettings.find((s) => s.role === 'student');
            }

            if (settings.enabled) {
              this.showQuestionIcon = true;
            }
          });
      },
      createLocalVideoTrack() {
        this.$nextTick(() => {
          const user = this.getCurrentDiscussion.turns_in_circle.find(
            (t) =>
              t.participation.user.id === this.getProfile.id &&
              t.turn_type !== 'video_audio_on'
          );

          if (user) {
            const user_id = user.participation.user.id;

            const localPlayerContainer = document.createElement('div');
            localPlayerContainer.id = user_id;
            localPlayerContainer.style.top = '0';
            localPlayerContainer.style.height = '100%';
            localPlayerContainer.style.width = '100%';
            localPlayerContainer.style.position = 'absolute';
            localPlayerContainer.style.borderRadius = '0 0 4px 4px';

            const discussion_circle = document.getElementById(
              `circle-list-${user_id}`
            );
            discussion_circle.appendChild(localPlayerContainer);
            this.rtc.localVideoTrack.play(localPlayerContainer);

            const trackID = this.rtc.localVideoTrack.getTrackId();
            const elementVideo = document.querySelector(
              `[id^="agora-video-player-${trackID}"]`
            );
            elementVideo.style.borderRadius = '0 0 4px 4px';
            if (this.my_participation.video_is_enabled) {
              if (elementVideo) elementVideo.style.opacity = 100;
            } else {
              if (elementVideo) elementVideo.style.opacity = 0;
            }
          }
        });
      },
      saveSettingsTab: async function () {
        if (!this.is_button_save_setting_enable) {
          await EventBus.$emit('save-settings');
          this.is_confirm_settings_modal_open = false;
        }
      },
      destroyLocalCamera() {
        const video = document.querySelector('.local-camera');
        if (video && video.srcObject) {
          let tracks = video.srcObject.getTracks();
          tracks.forEach((track) => {
            track.stop();
          });
        }
      },
      closeParticipation() {
        this.isOpenParticipant = false;
        this.$router.replace({
          name: this.$route.name,
          params: this.$route.params,
          query: {},
        });
      },
      handleOpenStartFollowUP: async function (newFollowUpType) {
        const payload = {
          title: "",
          prompt: WrittenFollowUpTemplate,
          type: newFollowUpType,
          instructions: "Review the resources and the questions before the Verbal RoundTables starts. Take notes about answers that you have to the questions, and use your notepad to keep track of your ideas as the conversation unfolds.",
          user_id: this.getProfile.id,
          courses: [this.getCurrentDiscussion.course_id],
          is_follow_up: true,
          follow_up_from_id: this.getCurrentDiscussion.id,
          follow_up_from_type: this.getCurrentDiscussion.type,
          is_generated: false
        };

        if (!this.getProfile.is_paid && this.getProfile.activity_passes === 0) {
          const toast = { type: 'Info', message: 'Sorry, you have no free RoundTables left! Delete an old RoundTable or upgrade your account!', time: 5000, upgrade: true };
          return EventBus.$emit('createToast', toast);
        }
        
        const draft = await Axios.post(`/api/drafts/draft`, payload);
        if (!draft) {
          throw new Error('Something happened starting roundtable creation! Please try again');
        }
        await this.$store.dispatch('useActivityPass');
        this.selectedRoundTableToFollow = { 
          id: this.getCurrentDiscussion.id,
          draftId: draft.data.id,
          currentRoundtableType: this.getCurrentDiscussion.type, 
          type: newFollowUpType, 
          course: this.getCourseDetail,
        };
        this.isFollowUpOpen = true;
      },
      closeTemplateModal() {
        this.templates_modal = false;
        EventBus.$emit('assessment-focus');
      },
      onChangeNotepad(event) {
        /* this.toogle_notepad = event.value; */
        this.toogle_notepad = event;
        EventBus.$emit('resize-section', this.toogle_notepad);
      },
      onChangeParticipations(/* event */) {
        this.toogle_participations = !this.toogle_participations;
        EventBus.$emit('resize-section', this.toogle_participations);
      },
      onChangeSidebar() {
        if (this.hasTeacherPermissionsInCourse) {
          this.toogle_participations = !this.toogle_participations;
          EventBus.$emit('resize-section', this.toogle_participations);
          return;
        }
        this.toogle_notepad = !this.toogle_notepad;
        EventBus.$emit('resize-section', this.toogle_notepad); 
      }
    },

    mounted: async function () {
      
      const { liveDiscussionID, courseID } = this.$route.params;
      if (!this.getCourseDetail.id || this.getCourseDetail.id !== courseID) {
        await this.$store.dispatch('enterCourse', courseID);
      }

      await this.setUpLiveDiscussion(liveDiscussionID, courseID);
      this.showWelcomeVideosOnSamples();
      this.showRTWelcomeModalQuestionIcon();

      await this.handleExecuteTranscription();
      await this.handleExecuteNormalTranscription();

      if (this.discussion_is_live) {
        setInterval(() => {
          this.verbalDiscussionDuration();
        }, 1000);
      }

      if (this.recognition !== null) {
        this.recognition.onresult = async (event) => {
          this.recognitionStart = true;
          this.speechStartAt = moment();
          const transcript = event.results[event.results.length - 1][0].transcript;
          const transcript_metadata = {
            user_id: this.getProfile.id,
            start_time: this.speechStartAt,
            content: transcript,
            live_discussion_id: this.getCurrentDiscussion.id,
            participation_id: this.hasTeacherPermissionsInCourse === true ? null : this.getMyParticipation.id
          };
          await handleSaveTranscription(transcript_metadata);     
        };

        this.recognition.onend = () => {
          this.recognitionStart = false;
        };

        this.recognition.onaudiostart = () => {
          if (this.rtc.localAudioTrack.enabled === true) {
            EventBus.$emit('capturing-transcription', true);
            console.log('Audio capturing started');
          }
        };

        this.recognition.onaudioend = () => {
          if (this.rtc.localAudioTrack.enabled === true) {
            EventBus.$emit('capturing-transcription', false);
            console.log('Audio capturing ended');
          }
        };

        this.recognition.onerror = (error) => {
          const toast = { message: '', type: 'Error' };
          if (error.error === 'no-speech') {
            toast.message = 'No speech was detected from your microphone. Transcription will stop.';
          } else if (error.error === 'audio-capture') {
            toast.message = 'No microphone was found. Transcription will not work.';
          } else if (error.error === 'not-allowed') {
            toast.message = 'Permission to access the microphone was denied. Transcription will not work.';
          } else {
            toast.message = 'An error occurred. Contact support.';
          }
          EventBus.$emit('createToast', toast);
          console.error(error);
        };
      }

      if (this.recognitionNormal !== null) {

        this.recognitionNormal.onresult = async (event) => {
          this.recognitionStartNormal = true;
          const transcript = event.results[event.results.length - 1][0].transcript;
          const transcript_metadata = {
            user_id: this.getProfile.id,
            start_time: moment(),
            content: transcript,
            live_discussion_id: this.getCurrentDiscussion.id,
            participation_id: this.hasTeacherPermissionsInCourse === true ? null : this.getMyParticipation.id
          };
          await handleSaveTranscription(transcript_metadata);     
        };

        this.recognitionNormal.onend = () => {
          this.recognitionStartNormal = false;
        };

        this.recognitionNormal.onaudiostart = () => {
          EventBus.$emit('capturing-transcription', true);
          console.log('Audio capturing started');
        };

        this.recognitionNormal.onaudioend = () => {
          EventBus.$emit('capturing-transcription', false);
          console.log('Audio capturing ended');
        };

        this.recognitionNormal.onerror = (error) => {
          const toast = { message: '', type: 'Error' };
          if (error.error === 'no-speech') {
            toast.message = 'No speech was detected from your microphone. Transcription will stop.';
          } else if (error.error === 'audio-capture') {
            toast.message = 'No microphone was found. Transcription will not work.';
          } else if (error.error === 'not-allowed') {
            toast.message = 'Permission to access the microphone was denied. Transcription will not work.';
          } else {
            toast.message = `An error occurred. Contact support + ${error}`;
          }
          EventBus.$emit('createToast', toast);
          console.error(error);
        };
      }

      

      if (this.$route.name === 'live-discussion.index') {
        if (this.hasTeacherPermissionsInCourse && this.$route.query && this.$route.query.inviteModalOpen) {
          this.isInviteStudentModalOpen = true;
          this.$router.replace({ name: this.$route.name, query: { inviteModalOpen: true } });
        }
        else {
          if (this.getCurrentDiscussion.is_live) {
            this.goToTab('discussion');
          } else {
            this.goToTab('prompt');
          }
        }
      } else if (this.$route.name === 'live-discussion.discussion' && this.$route.query.from_feedback) {
        this.goToTab('discussion');
        this.$router.replace({
          name: this.$route.name,
          query: {
            from_feedback: true,
          },
        });
      } else if (this.$route.name === 'live-discussion.discussion' && this.$route.query.second_screen) {
        this.goToTab('discussion');
        this.$router.replace({
          name: this.$route.name,
          query: {
            second_screen: true,
          },
        });
        this.toggleFullscreen();
      } else if (this.$route.name === 'live-discussion.discussion' && this.$route.query.follow_up) {
        this.goToTab('discussion');
        this.$router.replace({
          name: this.$route.name,
          query: {
            follow_up: true,
          },
        });
        this.handleOpenStartFollowUP();
      } else if (this.$store.state.isFromNotification === true && this.getCurrentDiscussion.is_live === false) {
        this.goToTab('prompt');
      } else {
        const tab = this.$route.name.substring(this.$route.name.indexOf('.') + 1);
        this.goToTab(tab);
      }

      if (this.hasTeacherPermissionsInCourse) {
        this.toogle_participations = true;
        this.toogle_notepad = false;
      } else {
        this.toogle_notepad = true;
        this.toogle_participations = false;
      }

      const participation = this.getCurrentDiscussion.participations.find(
        (p) => p.user_id === this.getProfile.id
      );
      if (participation) {
        this.$store.dispatch('openLiveRoundtable', {
          id: participation.id,
          rtId: this.getCurrentDiscussion.id,
        });
      }

      const current_speaker = this.getCurrentDiscussion.currently_active;
      if (this.getProfile.id === current_speaker) {
        const toast = {
          type: 'Info',
          message: 'You are the Speaker',
          time: 5000,
        };
        EventBus.$emit('createToast', toast);
      }

      if (
        this.getCurrentDiscussion.edlink_integrations &&
        this.getCurrentDiscussion.edlink_integrations.length &&
        this.getProfile.edlink_integrations &&
        this.getProfile.edlink_integrations.length &&
        this.getMyParticipation.edlink_integrations &&
        this.getMyParticipation.edlink_integrations.length &&
        this.getMySubscription.role === 'student'
      ) {
        await this.submitIntegrationSubmission();
      }

      if (
        this.getProfile.google_integration &&
        this.getCurrentDiscussion.google_classroom_course_work_id &&
        this.getMyParticipation.google_classroom_integration &&
        this.getMyParticipation.google_classroom_integration.connected &&
        this.getMySubscription.role === 'student'
      ) {
        if (!this.getProfile.google_integration.connected) {
          await this.connectGoogleAccount();
        } else if (
          this.getProfile.google_integration.connected &&
          !this.getMyParticipation.google_classroom_integration.submission_id
        ) {
          await this.submitGoogleClassroomSubmission();
        }
      }

      const role = this.getProfile.role;
      if (role === 'teacher' && this.getProfile.is_onboarded === false) {
        EventBus.$emit('open-welcome-modal');
      }

      const toast_start_follow_up = {
        type: 'Info',
        message:
          'More than 80% of students have participated. Do you want to create a follow-up question to this RoundTable?',
        link: {
          course_id: this.getCourseDetail.id,
          live_discussion_id: this.getCurrentDiscussion.id,
          follow_up: true,
        },
      };
      if (
        this.hasTeacherPermissionsInCourse &&
        this.getCurrentDiscussion.is_start_follow_up_notification === true &&
        this.getCurrentDiscussion.sequence_id === null
      ) {
        EventBus.$emit('createToast', toast_start_follow_up);
      }

      if (this.getProfile.transcription_feature_flag === false && this.hasTeacherPermissionsInCourse) {
        setTimeout(() => {
          this.showGeniePopover = true;
        }, 3000);
        
      }

      EventBus.$on('integrationConnected', (integration) => {
        this.integration = integration;
        this.is_integration_connected = true;
      });
      EventBus.$on('goToLiveDiscussion', () => {
        this.goToTab('discussion');
      });
      EventBus.$on('integrationDisconnected', async () => {
        this.is_integration_connected = false;
        await this.loadPage(liveDiscussionID, courseID);
      });
      EventBus.$on('openAssessmentModal', () => {
        this.show_integrations_modal = true;
      });
      EventBus.$on('rtIntegrationAssignment', (sync_assessment) => {
        this.show_integrations_modal = sync_assessment;
      });
      EventBus.$on('selectedIntegrationSync', (sync_assessment) => {
        this.show_integrations_modal = false;
        this.selected_integration = 'google-classroom';
        this.edLink = false;
        this.logo = '/static/google_class_round.png';
        this.sync_assessment = sync_assessment;
        return;
      });
      EventBus.$on(
        'selectedIntegrationSyncEdLink',
        (sync_assessment, integration_name) => {
          this.show_integrations_modal = false;
          this.selected_integration = integration_name;
          this.edLink = true;
          this.logo = `/static/integrations/${
            integration_name.charAt(0).toUpperCase() + integration_name.slice(1)
          }.png`;
          this.sync_assessment = sync_assessment;
          return;
        }
      );
      EventBus.$on('statusDiscussionChange', () => {
        if (this.$route.name !== 'live-discussion.discussion') {
          this.isOpenModalDiscussionStatusChange = true;
        }
      });
      EventBus.$on('currently_speaker', () => {
        if (this.$route.name !== 'live-discussion.discussion') {
          const toast = {
            type: 'Info',
            message: '"You are the Speaker". Go to the Discussion Tab',
            time: 5000,
          };
          EventBus.$emit('createToast', toast);
        }
      });
      EventBus.$on('joinAudioDiscussion', async () => {
        this.startLiveCall();
      });
      EventBus.$on('end-live-audio', async () => {
        if (this.hasTeacherPermissionsInCourse) {
          this.destroyLocalCamera();
        }
        await this.leaveCall();
        this.joined_audio_discussion = false;
      });
      EventBus.$on('open-invite-google-classroom-modal', () => {
        this.open_google_classroom_dialog = true;
      });
      EventBus.$on('openAssessmentStudent', (participation, tabToGo) => {
        if (tabToGo !== '' || tabToGo !== null || tabToGo !== undefined) {
          this.goToTab('discussion');
        }
        this.$router.replace({
          name: this.$route.name,
          params: this.$route.params,
          query: { feedback: 'open' },
        });
        this.openParticipant(true, participation);
      });
      EventBus.$on('showTemplateModal', () => {
        this.templates_modal = true;
      });
      EventBus.$on('openInviteStudentsModal', () => {
        this.isInviteStudentModalOpen = true;
      });
      EventBus.$on('handleTab', (tab) => {
        this.goToTab(tab);
      });
      EventBus.$on('showRountableWelcomeModal', () => {
        this.showRTWelcomeModal();
      });
      EventBus.$on('videoOff', () => {
        this.videoOff();
      });
      EventBus.$on('videoOn', () => {
        this.videoOn();
      });
      EventBus.$on('muteAudio', () => {
        this.muteAudio();
      });
      EventBus.$on('unmuteAudio', () => {
        this.unmuteAudio();
      });
      EventBus.$on('handleOpenStartDiscussionModal', () => {
        this.isStartDiscussionModalOpen = true;
      });
      EventBus.$on('handleEndDiscussion', () => {
        this.isEndDiscussionModalOpen = true;
      });
      EventBus.$on('handleOpenPolls', () => {
        this.isToShowPolls = true;
      });
      EventBus.$on('handleOpenCriteria', () => {
        this.isToShowCriteria = true;
      });
      EventBus.$on('handleOpenGuidingQuestions', () => {
        this.isToShowGuidingQuestions = true;
      });
      EventBus.$on('handleStartShareScreen', () => {
        this.handleStartScreenShare();
      });
      EventBus.$on('handleStopShareScreen', () => {
        this.handleStopShareScreen();
      });
      EventBus.$on('handleLeaveChannel', async () => {
        if (this.screenRTC.client !== null) {
          await this.screenRTC.client.leave();
        }
      });

      EventBus.$on('handleOpenFollowUpQuestionsCurrentModal', async (followUpQuestions) => {
        this.followUpQuestionsCurrent = followUpQuestions;
        this.isFollowUpQuestionsModalOpen = true;
      });

      EventBus.$on('handleOpenGuidingQuestionsModal', async () => {
        if (this.hasTeacherPermissionsInCourse) {
          const guiding_questions = this.getCurrentDiscussion.toggleable_guiding_questions.concat(this.getCurrentDiscussion.genie_recap.questions);
          await this.$store.dispatch('saveLiveDiscussionSettings', { id: this.getCurrentDiscussion.id, toggleable_guiding_questions: guiding_questions });
          this.isToShowGuidingQuestions = true;
        }
      });
      EventBus.$on('handleOpenEditWordCloudVR', (keywords) => {
        this.keywordData = keywords;
        this.isToShowEditWordCloud = true;
      });

      EventBus.$on('handleOpenExportData', () => {
        this.show_export_data_modal = true;
      });
    },
    beforeRouteUpdate(to, from, next) {
      if (to.query.follow_up === true) {
        this.handleOpenStartFollowUP();
      }
      this.stepsToLeaveRoute(to, from, next);
    },
    beforeRouteLeave: async function (to, from, next) {
      this.destroyLocalCamera();
      if (this.hasTeacherPermissionsInCourse && this.getCurrentDiscussion.is_screen_sharing_active) {
        try {
          await axios.put(`/api/live_discussions/${this.getCurrentDiscussion.id}/stop-share-screen`);
          this.screenRTC.client.unpublish([this.screenTracks.screenTrack]);
        } catch (error) {
          console.error('Error stopping media tracks:', error);
        }
      }

      this.leaveLiveDiscussionSocket(this.getCurrentDiscussion.id);
      next();
    },
    async beforeDestroy() {
      this.stopRecognition();
      if (this.hasTeacherPermissionsInCourse && this.getCurrentDiscussion.is_screen_sharing_active) {
        try {
          await axios.put(`/api/live_discussions/${this.getCurrentDiscussion.id}/stop-share-screen`);
          this.screenRTC.client.unpublish([this.screenTracks.screenTrack]);
        } catch (error) {
          console.error('Error stopping media tracks:', error);
        }
      }
      EventBus.$emit('close_toast');
      this.leaveLiveDiscussionSocket(this.getCurrentDiscussion.id);
      if (this.getCurrentDiscussion.audio_discussion_is_enabled === true) {
        localStorage.removeItem('preferredAudioDevice');
        localStorage.removeItem('preferredVideoDevice');

        if (this.getMyParticipation.audio_is_enabled)
          toggleOwnAudio(this.getCurrentDiscussion.id);
        if (this.getMyParticipation.video_is_enabled)
          toggleOwnVideo(this.getCurrentDiscussion.id);

        
        await this.leaveCall();
        await this.destroyLocalCamera();
      }

      EventBus.$off('joinAudioDiscussion');
      EventBus.$off('open-invite-google-classroom-modal');
      EventBus.$off('integrationConnected');
      EventBus.$off('integrationDisconnected');
      EventBus.$off('goToLiveDiscussion');
      EventBus.$off('rtIntegrationAssignment');
      EventBus.$off('selectedIntegrationSync');
      EventBus.$off('statusDiscussionChange');
      EventBus.$off('currently_speaker');
      EventBus.$off('end-live-audio');
      EventBus.$off('openAssessmentStudent');
      EventBus.$off('openAssessmentModal');
      EventBus.$off('selectedIntegrationSyncEdLink');
      EventBus.$off('showTemplateModal');
      EventBus.$off('openInviteStudentsModal');
      EventBus.$off('handleTab');
      EventBus.$off('showRountableWelcomeModal');
      EventBus.$off('videoOff');
      EventBus.$off('videoOn');
      EventBus.$off('muteAudio');
      EventBus.$off('unmuteAudio');
      EventBus.$off('handleOpenStartDiscussionModal');
      EventBus.$off('handleEndDiscussion');
      EventBus.$off('handleOpenPolls');
      EventBus.$off('handleOpenGuidingQuestions');
      EventBus.$off('handleOpenFollowUpQuestionsCurrentModal');
      EventBus.$off('handleOpenCriteria');
      EventBus.$off('handleOpenExportData');
    },
  });
</script>

<style scoped>
  .live-discussion-body {
    min-height: calc(100vh - 148px);
    background-color: #fafafa;
  }
  .tab-body {
    min-height: calc(100vh - 110px);
    height: auto;
    padding-bottom: 8px;
  }

  .discussion-body {
    height: calc(100vh - 136px);
    background-color: white;
  }

  .tab-body-fullscreen {
    height: 100%;
    background-color: white;
  }
  .max-height-text-area {
    max-height: calc(100vh - 200px);
  }

  .max-h-container-participants {
    max-height: calc(100vh - 120px);
    overflow-y: auto;
  }
  .max-h-container-participants-fullscreen {
    max-height: 100vh;
    overflow-y: auto;
  }

</style>
<style lang="scss" scoped>
  input[type='checkbox'] {
    width: 1.1rem !important;
    height: 1.1rem !important;
    -webkit-appearance: none;
    -moz-appearance: none;
    -o-appearance: none;
    appearance: none;
    box-shadow: none;
    font-size: 0.8rem;
    text-align: center;
    line-height: 1rem;
    border-radius: 0.25rem;
    border: solid 1px #cfcfcf;
    --tw-bg-opacity: 1;
    background-color: rgba(243, 244, 246, var(--tw-bg-opacity));
  }
  input[type='checkbox']:checked {
    background-color: #72ccd7;
    border: solid 1px #72ccd7;
  }
  input[type='checkbox']:checked::after {
    font-family: 'Font Awesome 5 Free';
    content: '\f00c';
    font-weight: 900;
    color: white;
    line-height: 1rem;
  }
</style>



// WEBPACK FOOTER //
// liveDiscussion.vue?d85285ec